import _ from 'lodash'
import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'

import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded'
import IndeterminateCheckBoxRoundedIcon from '@mui/icons-material/IndeterminateCheckBoxRounded'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReceiptIcon from '@mui/icons-material/Receipt'
import AttachEmailIcon from '@mui/icons-material/AttachEmail'
import MedicationRoundedIcon from '@mui/icons-material/MedicationRounded'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import Document from '@/components/group/Document'
import FilePreviewDialog from '@/components/ui/dialogs/FilePreviewDialog/FilePreviewDialog'
import UploadButton from '@/components/ui/buttons/UploadButton'

import ContentHeader from './ContentHeader'
import GenerateFspDialog, { GenerateFSPFormValues } from './GenerateFSPDialog'
import GenerateReportDialog, { GenerateReportDialogProps, GenerateReportFormValues } from './GenerateReportDialog'
import QrCodeDialog from './QrCodeDialog'
import EmailDocumentsDialog from './EmailDocuments'
import { useIsWebView } from '@/hooks/useSizeBreakpoints'
import { Typography } from '@mui/material'

enum DocumentCategory {
  AUDIO = 'audio',
  DOCUMENT = 'document',
  EXCEL = 'excel',
  PDF = 'pdf',
  PHOTO = 'photo',
  VIDEO = 'video',
  OTHER = 'other',
}

export type DocumentsHandle = {
  toggleEmailDocumentsDialog: () => void
}

export type DocumentsProps = {
  documents: {
    key: string
    name: string
    type: string
    src: string
  }[]
  dictaphoneSentences: string[]
  conclusionsSentences: string[]
  disableEPrescription?: boolean
  disableDesmosAct?: boolean
  emails?: string[]
  isDeleting?: boolean
  isDoctor?: boolean
  isSendingByEmail?: boolean
  isExporting?: boolean
  isGeneratingReport?: boolean
  isLocal?: boolean
  initialReportValues: GenerateReportFormValues
  consultationId?: string
  consultationEndDate?: string | Date
  patientDob?: string | Date
  isGeneratingFSP?: boolean
  /** @deprecated No longer used — dialog fetches acts from the API */
  initialFSPValues?: GenerateFSPFormValues
  onDelete?: (documents: DocumentsProps['documents']) => void
  onExport?: (documents: DocumentsProps['documents']) => void
  onGenerateReport?: (reportValues: GenerateReportFormValues) => void
  onGenerateFsp?: (values: GenerateFSPFormValues) => void
  onGenereatePrescriptionButtonClick?: () => void
  onGenerateDesmosActButtonClick?: () => void
  onPrintImages?: (documents: DocumentsProps['documents']) => void
  onQrClick?: () => void
  onSaveFileAnnotation?: (file: File, key: string) => void
  onSaveFileName?: (filename: string, key: string) => void
  onUpload?: (file: File) => void
  onRefresh?: () => void
  onSendDocumentsByEmail?: (emails: string[], documents: DocumentsProps['documents'], pdfPassword?: string) => void
  onOcrDocument?: (fileKey: string) => void
  isProcessingOcr?: boolean
  processingOcrFileKey?: string | null
  qrCode?: string
  readOnly?: boolean
  formSubmissionOptions?: GenerateReportDialogProps['formSubmissionOptions']
}

const documentCategoryWeight: Record<DocumentCategory, number> = {
  [DocumentCategory.AUDIO]: 0,
  [DocumentCategory.DOCUMENT]: 1,
  [DocumentCategory.EXCEL]: 2,
  [DocumentCategory.PDF]: 3,
  [DocumentCategory.PHOTO]: 4,
  [DocumentCategory.VIDEO]: 5,
  [DocumentCategory.OTHER]: 6,
}

const typeCategories: Record<string, DocumentCategory> = {
  jpeg: DocumentCategory.PHOTO,
  jpg: DocumentCategory.PHOTO,
  png: DocumentCategory.PHOTO,
  pdf: DocumentCategory.PDF,
  mp3: DocumentCategory.AUDIO,
  wav: DocumentCategory.AUDIO,
  m4a: DocumentCategory.AUDIO,
  mpeg: DocumentCategory.AUDIO,
  webm: DocumentCategory.AUDIO,
  mov: DocumentCategory.AUDIO,
  mp4: DocumentCategory.VIDEO,
  mkv: DocumentCategory.VIDEO,
  docx: DocumentCategory.DOCUMENT,
  txt: DocumentCategory.DOCUMENT,
  xlsx: DocumentCategory.EXCEL,
}

type DocumentsGridProps = {
  documents: DocumentsProps['documents']
  documentNumberColumns: number
  readOnly: boolean
  selectedDocuments: DocumentsProps['documents']
  handleChangeDocumentSelection: (document: DocumentsProps['documents'][0]) => (event: React.ChangeEvent, checked: boolean) => void
  onOpenPreview: (index: number) => void
  allDocuments: DocumentsProps['documents']
}

// Helper function to get file extension
const getFileExtension = (filename: string): string => {
  const parts = filename.split('.')
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : ''
}

// Helper function to get category from mime type
const getCategoryFromMimeType = (mimeType: string): DocumentCategory => {
  if (mimeType.startsWith('image/')) return DocumentCategory.PHOTO
  if (mimeType.startsWith('audio/')) return DocumentCategory.AUDIO
  if (mimeType.startsWith('video/')) return DocumentCategory.VIDEO
  if (mimeType.startsWith('application/pdf')) return DocumentCategory.PDF
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return DocumentCategory.EXCEL
  if (mimeType.includes('document') || mimeType.includes('text/')) return DocumentCategory.DOCUMENT
  return DocumentCategory.OTHER
}

export const Documents = React.forwardRef(function Documents(props: DocumentsProps, ref: React.Ref<DocumentsHandle>) {
  const {
    dictaphoneSentences,
    conclusionsSentences,
    documents = [],
    onOcrDocument,
    isProcessingOcr,
    processingOcrFileKey,
    consultationId,
    consultationEndDate,
    patientDob,
    disableEPrescription = true,
    disableDesmosAct = true,
    emails = [],
    isDeleting,
    isDoctor = false,
    isExporting,
    isSendingByEmail = false,
    isGeneratingReport = false,
    isGeneratingFSP = false,
    isLocal = false,
    initialFSPValues,
    initialReportValues,
    onDelete,
    onExport,
    onGenerateFsp,
    onGenerateReport,
    onGenereatePrescriptionButtonClick,
    onGenerateDesmosActButtonClick,
    onPrintImages,
    onSaveFileAnnotation,
    onSaveFileName,
    onUpload,
    onRefresh,
    onSendDocumentsByEmail,
    qrCode,
    readOnly = false,
    formSubmissionOptions = [],
  } = props
  const { width, ref: containerRef } = useResizeDetector()
  const { t } = useTranslation()

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)
  const [emailDocumentsDialogOpen, setEmailDocumentsDialogOpen] = useState<boolean>(false)
  const [generateReportDialogOpen, setGenerateReportDialogOpen] = useState<boolean>(false)
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState<boolean>(false)
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentsProps['documents']>([])
  const [generateFSPDialogOpen, setGenerateFSPDialogOpen] = useState<boolean>(false)
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false)
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(-1)

  const groupedDocuments = useMemo(() => {
    return _.groupBy(documents, (doc) => {
      // Try to get category from file extension first
      const extension = getFileExtension(doc.name)
      if (extension && typeCategories[extension.toLowerCase()]) {
        return typeCategories[extension.toLowerCase()]
      }

      // If no extension or unknown extension, try to determine from mime type
      return getCategoryFromMimeType(doc.type) || DocumentCategory.OTHER
    })
  }, [documents])

  useImperativeHandle(ref, () => ({
    toggleEmailDocumentsDialog,
  }))

  useEffect(() => {
    // reset selectedDocuments after delete
    if (!!selectedDocuments.length && !_.intersectionBy(documents, selectedDocuments, 'key').length) {
      setSelectedDocuments([])
    }
  }, [documents, selectedDocuments])

  // close generate report dialog after it is created successfully
  useEffect(() => {
    if (!isGeneratingReport) {
      setGenerateReportDialogOpen(false)
    }
  }, [isGeneratingReport])

  const handleChangeDocumentSelection =
    (document: { key: string; name: string; type: string; src: string }) => (event: React.ChangeEvent, checked: boolean) => {
      setSelectedDocuments((prevSelectedDocuments = []) => {
        return checked
          ? [...prevSelectedDocuments, document]
          : _.filter(prevSelectedDocuments, (prevSelectedDocument) => {
              return !_.isEqual(prevSelectedDocument, document)
            })
      })
    }

  const handleDelete = () => {
    onDelete?.(selectedDocuments)
  }

  const handleExport = () => {
    onExport?.(selectedDocuments)
  }

  const handleGenerateReport = (reportValues: GenerateReportFormValues) => {
    onGenerateReport?.(reportValues)
  }

  const handlePrintImages = () => {
    onPrintImages?.(_.filter(selectedDocuments, (document) => _.some(['png', 'jpg', 'jpeg'], (ext) => document.type.toLowerCase() === ext)))
  }

  const handleSelectAll = () => {
    setSelectedDocuments(documents.length === selectedDocuments.length ? [] : documents)
  }

  const handleSendDocumentsByEmail = (emails: string[], pdfPassword?: string) => {
    onSendDocumentsByEmail?.(emails, selectedDocuments, pdfPassword)
  }

  const toggleDeleteConfirmationDialog = () => {
    setDeleteConfirmationDialogOpen((prevState) => !prevState)
  }

  const toggleEmailDocumentsDialog = () => {
    setEmailDocumentsDialogOpen(!emailDocumentsDialogOpen)
  }

  const toggleGenerateReportDialog = () => {
    setGenerateReportDialogOpen(!generateReportDialogOpen)
  }

  const toggleQrCodeDialog = () => {
    setQrCodeDialogOpen(!qrCodeDialogOpen)
  }

  const handleRefresh = () => {
    onRefresh?.()
  }

  const toggleGenerateFSPDialog = () => {
    setGenerateFSPDialogOpen(!generateFSPDialogOpen)
  }

  const handleGenerateFSP = (_actIds: string[], legacyActs: import('@/core/models/consultation.model').Act[]) => {
    onGenerateFsp?.({ Acts: legacyActs })
    toggleGenerateFSPDialog()
  }

  const handleOpenPreview = (index: number) => {
    setCurrentPreviewIndex(index)
    setPreviewDialogOpen(true)
  }

  const handleClosePreview = () => {
    setPreviewDialogOpen(false)
    setCurrentPreviewIndex(-1)
  }

  const handleNextDocument = () => {
    if (currentPreviewIndex < documents.length - 1) {
      setCurrentPreviewIndex(currentPreviewIndex + 1)
    }
  }

  const handlePreviousDocument = () => {
    if (currentPreviewIndex > 0) {
      setCurrentPreviewIndex(currentPreviewIndex - 1)
    }
  }

  const handleSaveFileNameFromPreview = (filename: string) => {
    if (currentPreviewIndex >= 0) {
      const currentDoc = documents[currentPreviewIndex]
      onSaveFileName?.(filename, currentDoc.key)
    }
  }

  const handleSaveFileAnnotationFromPreview = (file: File) => {
    if (currentPreviewIndex >= 0) {
      const currentDoc = documents[currentPreviewIndex]
      onSaveFileAnnotation?.(file, currentDoc.key)
    }
  }

  // close generate report dialog after it is created successfully
  useEffect(() => {
    if (!generateReportDialogOpen) {
      setGenerateReportDialogOpen(false)
    }
  }, [generateReportDialogOpen])

  let containerWidth = width || 0
  let documentNumberColumns = 2
  switch (true) {
    case containerWidth > 600:
      documentNumberColumns = 4
      break
    case containerWidth > 450:
      documentNumberColumns = 3
      break
    default:
      break
  }

  let isAllDocumentsSelected = documents.length === selectedDocuments.length

  const isWebView = useIsWebView()
  const minWidth = isWebView ? '280px' : '480px'

  return (
    <Container maxWidth="lg" sx={{ p: '0 !important' }}>
      <Stack ref={containerRef} justifyContent="center" sx={{ minHeight: '100%' }}>
        <Paper
          sx={[
            {
              minWidth: minWidth,
              minHeight: '300px',
              p: '24px',
            },
          ]}
        >
          <ContentHeader
            title={t('page.liveConsultation.documents.title')}
            subtitle={isLocal ? t('page.liveConsultation.documents.showQrcodeButton.tooltip') : t('page.liveConsultation.documents.subtitle')}
          />
          <Stack spacing={2} sx={{ p: '10px 0' }}>
            <Stack direction="row" justifyContent="flex-end" flexWrap="wrap" columnGap={2} rowGap={2}>
              <Tooltip title={<span>{t('common:button.refresh.tooltip')}</span>} placement="top">
                <span>
                  <Button onClick={handleRefresh} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    <RefreshIcon />
                  </Button>
                </span>
              </Tooltip>
              {!readOnly && (
                <Tooltip title={<span>{t('page.liveConsultation.documents.showQrcodeButton.tooltip')}</span>} placement="top">
                  <span>
                    <Button onClick={toggleQrCodeDialog} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                      <QrCode2RoundedIcon />
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!readOnly && (
                <Tooltip title={<span>{t('page.liveConsultation.documents.generateReportButton.tooltip')}</span>} placement="top">
                  <Button onClick={toggleGenerateReportDialog} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    <InsertDriveFileIcon />
                  </Button>
                </Tooltip>
              )}
              {!readOnly && isDoctor && !disableEPrescription && (
                <Tooltip title={<span>{t('page.liveConsultation.documents.generatePrescriptionButton.tooltip')}</span>} placement="top">
                  <Button onClick={onGenereatePrescriptionButtonClick} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    <MedicationRoundedIcon />
                  </Button>
                </Tooltip>
              )}
              {!readOnly && !disableDesmosAct && (
                <Tooltip title={<span>{t('page.liveConsultation.documents.generateDesmosActButton.tooltip')}</span>} placement="top">
                  <Button onClick={onGenerateDesmosActButtonClick} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    <AccountBalanceIcon />
                  </Button>
                </Tooltip>
              )}
              {!readOnly && (
                <Tooltip title={<span>{t('page.liveConsultation.documents.generateFSPButton.tooltip')}</span>} placement="top">
                  <span>
                    <Button disabled={!isDoctor} onClick={toggleGenerateFSPDialog} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                      <ReceiptIcon />
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={<span>{t('page.liveConsultation.documents.sendDocumentsByEmail.tooltip')}</span>} placement="top">
                <span>
                  <Button disabled={!selectedDocuments.length} onClick={toggleEmailDocumentsDialog} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    <AttachEmailIcon />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={<span>{t('page.liveConsultation.documents.printImagesButton.tooltip')}</span>} placement="top">
                <span>
                  <Button disabled={!selectedDocuments.length} onClick={handlePrintImages} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    <PrintRoundedIcon />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={<span>{t('page.liveConsultation.documents.exportDocumentsButton.tooltip')}</span>} placement="top">
                <span>
                  <Button disabled={!selectedDocuments.length || isExporting} onClick={handleExport} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                    {isExporting ? <CircularProgress size={18} /> : <DriveFileMoveRoundedIcon />}
                  </Button>
                </span>
              </Tooltip>
              {!readOnly && (
                <Tooltip
                  title={<span>{t('page.liveConsultation.documents.deleteDocumentButton.tooltip', { count: selectedDocuments.length })}</span>}
                  placement="top"
                >
                  <span>
                    <Button
                      disabled={!selectedDocuments.length || isDeleting}
                      onClick={toggleDeleteConfirmationDialog}
                      size="small"
                      variant="chip"
                      sx={{ minWidth: 'unset' }}
                    >
                      {isDeleting ? <CircularProgress size={18} /> : <DeleteRoundedIcon />}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!readOnly && (
                <Tooltip
                  title={
                    <span>
                      {t(`page.liveConsultation.documents.${isAllDocumentsSelected ? 'unSelectAllDocumentsButton' : 'selectAllDocumentsButton'}.tooltip`)}
                    </span>
                  }
                  placement="top"
                >
                  <span>
                    <Button disabled={!documents.length} onClick={handleSelectAll} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                      {isAllDocumentsSelected ? <IndeterminateCheckBoxRoundedIcon /> : <CheckBoxRoundedIcon />}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
            <Box>
              <Grid container spacing={2}>
                {!readOnly && (
                  <Grid item xs={12 / documentNumberColumns}>
                    <UploadButton onUpload={onUpload} />
                  </Grid>
                )}
                {(Object.keys(groupedDocuments) as DocumentCategory[])
                  .sort((a, b) => documentCategoryWeight[a] - documentCategoryWeight[b])
                  .map((type) => (
                    <Grid key={type} item xs={12}>
                      <Typography variant="h5" color="steelblue" mb={2} textTransform="capitalize">
                        {type}
                      </Typography>
                      <DocumentsGrid
                        documents={groupedDocuments[type]}
                        documentNumberColumns={documentNumberColumns}
                        readOnly={readOnly}
                        selectedDocuments={selectedDocuments}
                        handleChangeDocumentSelection={handleChangeDocumentSelection}
                        onOpenPreview={handleOpenPreview}
                        allDocuments={documents}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          </Stack>
        </Paper>
        <DeleteConfirmationDialog
          isDeleting={isDeleting}
          onClose={toggleDeleteConfirmationDialog}
          onDelete={handleDelete}
          open={deleteConfirmationDialogOpen}
          title={t('dialog.deleteConfirmation.documents.title', { count: selectedDocuments.length })}
          message={t('dialog.deleteConfirmation.documents.message')}
        />
        <EmailDocumentsDialog
          isSending={isSendingByEmail}
          emailOptions={emails}
          open={emailDocumentsDialogOpen}
          onClose={toggleEmailDocumentsDialog}
          onSend={handleSendDocumentsByEmail}
        />
        <GenerateReportDialog
          dictaphoneSentences={dictaphoneSentences}
          conclusionSentences={conclusionsSentences}
          initialValues={initialReportValues}
          isDoctor={isDoctor}
          isGenerating={isGeneratingReport}
          onClose={toggleGenerateReportDialog}
          onGenerateReport={handleGenerateReport}
          open={generateReportDialogOpen}
          formSubmissionOptions={formSubmissionOptions}
        />
        <QrCodeDialog onClose={toggleQrCodeDialog} open={qrCodeDialogOpen} qrCodeValue={qrCode!} />
        <GenerateFspDialog
          consultationId={consultationId}
          consultationEndDate={consultationEndDate}
          patientDob={patientDob}
          isGenerating={isGeneratingFSP}
          onClose={toggleGenerateFSPDialog}
          onGenerateFSP={handleGenerateFSP}
          open={generateFSPDialogOpen}
        />
        {currentPreviewIndex >= 0 && documents[currentPreviewIndex] && (
          <FilePreviewDialog
            disableChangeFileName={readOnly}
            fileName={documents[currentPreviewIndex].name}
            fileType={documents[currentPreviewIndex].type}
            fileKey={documents[currentPreviewIndex].key}
            onClose={handleClosePreview}
            onSaveFileName={handleSaveFileNameFromPreview}
            onOcrDocument={onOcrDocument}
            isProcessingOcr={isProcessingOcr}
            processingOcrFileKey={processingOcrFileKey}
            open={previewDialogOpen}
            PhotoViewerProps={{
              disableAnnotation: readOnly,
              onSave: handleSaveFileAnnotationFromPreview,
            }}
            src={documents[currentPreviewIndex].src}
            onNext={currentPreviewIndex < documents.length - 1 ? handleNextDocument : undefined}
            onPrevious={currentPreviewIndex > 0 ? handlePreviousDocument : undefined}
          />
        )}
      </Stack>
    </Container>
  )
})

function DocumentsGrid(props: DocumentsGridProps) {
  const { documents, documentNumberColumns, readOnly, selectedDocuments, handleChangeDocumentSelection, onOpenPreview, allDocuments } = props
  return (
    <Grid container spacing={2}>
      {documents.map((document, index) => {
        // Find the index in the full documents array
        const globalIndex = allDocuments.findIndex((doc) => doc.key === document.key)
        return (
          <Grid key={index} item xs={12 / documentNumberColumns}>
            <Box sx={{ position: 'relative' }}>
              <Document
                disableAnnotation={readOnly}
                disableChangeName={readOnly}
                extension={document.type}
                fileKey={document.key}
                name={document.name}
                src={document.src}
                onOpenPreview={() => onOpenPreview(globalIndex)}
              />
              {!readOnly && (
                <Checkbox
                  checked={_.findIndex(selectedDocuments, (selectedDocument) => _.isEqual(selectedDocument, document)) !== -1}
                  size="small"
                  sx={{ position: 'absolute', top: '0', right: '0' }}
                  onChange={handleChangeDocumentSelection(document)}
                />
              )}
            </Box>
          </Grid>
        )
      })}
    </Grid>
  )
}

export default Documents
